import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setupSocket, enterBackend, socketOn } from '../../helpers';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import { useTranslation, Trans } from 'react-i18next';

import Header from '../../components/Header';
import PlayersManager from '../../components/PlayersList';

// Actions
import { gameActions } from '../../actions';

// Constants
import { roleNames } from '../../constants';

// Main Component: Form
const GameForm = () => {
  // State
  const [onboarding, setOnboarding] = useState(false);
  const [clickedLaunch, setClickedLaunch] = useState(false);
  const [item, setItem] = useState({});
  // const [changed, setChanged] = useState(false);
  // const [fetching, setFetching] = useState(false);

  // Hooks
  const dispatch = useDispatch();
  const game = useSelector((state) => state.game);
  const { t } = useTranslation();

  // Effects
  useEffect(() => {
    const onboarding = localStorage?.getItem('onboarding');
    if (onboarding === 'false') return;
    setOnboarding(true);
  }, []);

  // useEffect(() => {
  //   fetch(process.env.REACT_APP_API_URL + '/auth/oauth_provider')
  //     .then((res) => res.json())
  //     .then((data) => {
  //       setupSocket(data.io_domain, () => {
  //         if (item && item._id) {
  //           enterBackend({ game_id: item._id }, (gameData) => {
  //             console.log(`Connected to ${gameData._id} ws backend.`);
  //           });
  //           socketOn('player', () => {
  //             dispatch(gameActions.fetchGames(item._id));
  //           });
  //         }
  //       });
  //     });
  // }, [dispatch, item]);

  // Effect: Component initialisation
  useEffect(() => {
    if (game?.data) {
      setItem(game.data);
    }
  }, [game?.data]);

  // Methods
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newItem = {
      ...item,
      [name]: name === 'players_text' ? value.replace(/\t/g, ', ') : value,
      changed: true,
    };
    setItem(newItem);
    // setChanged(true);

    if (name === 'access_type') {
      dispatch(gameActions.updateGame(newItem));
    }
  };

  const submit = () => {
    dispatch(gameActions.updateGame(item));
  };

  const launch = () => {
    setClickedLaunch(true);
    dispatch(gameActions.launchGame(item));
  };

  const dississOnboarding = () => {
    setOnboarding(false);
    localStorage?.setItem('onboarding', false);
  };

  // Validation
  if (!item) return <div>{t('Loading...')}</div>;

  if (item?.status === 'launched' && clickedLaunch)
    return <Navigate to={'/games/' + item?._id + '/players'} />;

  // Logic

  // Helper function:
  // If no gindex is provided, find the group the player belongs to
  // and get the index of that group, use that index+1 as gindex
  const fillGindexFromGroups = (p, groupsArray) => {
    if (typeof p.gindex === 'undefined') {
      const groupIndex = groupsArray.findIndex((g) =>
        g.players.find((gp) => gp.email === p.email)
      );
      p.gindex = groupIndex !== -1 ? groupIndex + 1 : null;
    }
    return p;
  };

  // Helper function:
  // If no rindex is provided on the registered player,
  // find the player and get the rindex from the player element
  const fillRindexFromPlayers = (p, playersArray) => {
    if (typeof p.rindex === 'undefined') {
      const playerIndex = playersArray.findIndex((gp) => gp.email === p.email);
      p.rindex = playerIndex !== -1 ? playersArray[playerIndex].rindex : null;
    }
    return p;
  };

  const registeredPlayers = item?.registered
    ?.map((r) => fillGindexFromGroups(r, item?.groups)) // Fill gindex if not set
    .map((r) => fillRindexFromPlayers(r, item?.players)); // Fill rindex if not set

  // Determine if there are groups with more than the allowed number of players
  const groupCounts = registeredPlayers?.reduce((acc, rp) => {
    if (typeof rp.gindex === 'undefined') return acc;
    if (typeof acc[rp.gindex] === 'undefined') acc[rp.gindex] = 0;
    acc[rp.gindex]++;
    return acc;
  }, {});

  // Count the number of groups that have more than the allowed number of players
  const groupOver = groupCounts
    ? Object.keys(groupCounts).filter((g) => groupCounts[g] > item.ppg)
    : [];

  const okPPG = item?.registered?.length % item?.ppg === 0 ? true : false;
  const isGroupOver = groupOver.length > 0 ? true : false;
  // Check if a group has duplicate roles
  const hasDuplicateRoles = registeredPlayers?.some((rp) => {
    const roleCount = registeredPlayers.filter(
      (r) => r.rindex === rp.rindex && r.gindex === rp.gindex
    )?.length;
    return roleCount > 1;
  });
  const isSaving = item?.status === 'saving' ? true : false;
  const isLaunched = item?.status === 'launched' ? true : false;
  const isDisabled = isLaunched ? true : false;

  return (
    <div className=''>
      <Header />
      <h2 className='d-flex mt-3 sec-title fs-3'>
        {/* Title Left */}
        <span>
          {t('Simulation Setup')}{' '}
          <small
            style={{
              fontSize: '12px',
              padding: '5px 8px',
              verticalAlign: 'middle',
            }}
            className={
              item.status === 'saving'
                ? 'text-capitalize badge badge-pill text-bg-warning'
                : 'text-capitalize badge badge-pill text-bg-success'
            }>
            {item.status === 'saving' ? t('saving...') : t(item.status)}
          </small>
        </span>

        {/* Title Right */}
        <div className='ms-auto' style={{ lineHeight: 1 }}>
          <button
            className='btn btn-md btn-link text-dark me-5'
            onClick={() => setOnboarding(true)}>
            <small className='font-italic'>{t('Need help?')}</small>
          </button>
          <button
            className='btn btn-md btn-outline-primary'
            onClick={submit}
            disabled={isDisabled || isSaving}>
            {t('Save')}
          </button>
          <button
            className='btn btn-md btn-outline-success ms-3'
            onClick={launch}
            disabled={
              isDisabled || isGroupOver || hasDuplicateRoles || isSaving
            }>
            {t('Launch')}
          </button>
        </div>
      </h2>

      <div className='row mt-4'>
        {/* Right Panel: Player Management */}
        <div className='col-8 offset-2'>
          <div className='d-flex form-group'>
            <label className='d-block w-100 mb-2'>
              Registered Players{' '}
              <span>({item.registered ? item.registered.length : 0})</span>
            </label>

            {/* Error messages */}
            <div className='ms-auto text-end mb-1'>
              {!okPPG && (
                <small
                  style={{
                    fontSize: '12px',
                    padding: '5px 8px',
                    verticalAlign: 'middle',
                  }}
                  className='badge badge-pill bg-warning text-dark'>
                  {/* This game needs {item.ppg} players per group. Add more
                  players. */}
                  Incomplete groups are present.
                </small>
              )}

              {isGroupOver && (
                <small
                  style={{
                    fontSize: '12px',
                    padding: '5px 8px',
                    verticalAlign: 'middle',
                  }}
                  className='badge badge-pill bg-danger'>
                  Groups must have only {item.ppg} players
                </small>
              )}

              {hasDuplicateRoles && (
                <small
                  style={{
                    fontSize: '12px',
                    padding: '5px 8px',
                    verticalAlign: 'middle',
                  }}
                  className='badge badge-pill bg-danger'>
                  Groups must have {item.ppg} roles
                </small>
              )}
            </div>
          </div>

          {item.access_type === 'lti-hbp' && (
            <div className='mt-2 mb-5'>
              <PlayersManager roleLabels={roleNames} disabled={isDisabled} />
            </div>
          )}
        </div>
      </div>

      {/* Onboarding Modal */}
      <Modal
        isOpen={onboarding}
        toggle={dississOnboarding}
        centered={true}
        size='lg'>
        <ModalHeader>Onboarding</ModalHeader>
        <ModalBody className='text-center'>
          <div className='ratio ratio-16x9'>
            <iframe
              title='Game Setup Tutorial Video'
              src='https://player.vimeo.com/video/383603029'
              width='640'
              height='360'
              frameBorder='0'
              allow='autoplay; fullscreen'
              allowFullScreen></iframe>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className='btn btn-outline-primary'
            onClick={dississOnboarding}>
            Continue
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default GameForm;
